import React, { useCallback, useMemo } from "react";
import { graphql } from "react-relay";

import { useLazyLoadQuery } from "~/lib/relayUtil";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";

import type { TeamInputQuery } from "~/__generated__/TeamInputQuery.graphql";

export const teamsQuery = graphql`
	query TeamInputQuery($node: String) {
		viewer {
			teams(forNode: $node) {
				edges {
					node {
						id
						title
						archived
					}
				}
			}
		}
	}
`;

interface TeamInputProps {
	selected?: string;
	onChange(newTeam?: string): void;
	forTeam?: string;
	className?: string;
	disabled?: boolean;
}

export default function TeamInput({ selected, onChange, forTeam, className, disabled }: TeamInputProps) {
	const props = useLazyLoadQuery<TeamInputQuery>(teamsQuery, { node: forTeam }, { fetchPolicy: "store-or-network" });

	const selectTeam = useCallback(
		(teamData: SingleValue<SelectorOption>) => {
			onChange(teamData?.value);
		},
		[onChange],
	);
	const nonArchivedTeams = useMemo(
		() => (props.viewer.teams ? props.viewer.teams.edges.filter(edge => edge.node && !edge.node.archived) : []),
		[props.viewer.teams],
	);

	const selectedTeam = useMemo(() => {
		const team = selected && props.viewer.teams ? props.viewer.teams.edges.find(edge => edge.node.id === selected) : "";
		if (team) {
			return { label: team.node.title, value: team.node.id };
		} else return undefined;
	}, [props.viewer.teams, selected]);

	const options: SelectorOption[] = useMemo(() => {
		const teams = [{ label: "None", value: "" }];
		return teams.concat(nonArchivedTeams.map(team => ({ label: team.node.title, value: team.node.id })));
	}, [nonArchivedTeams]);

	return (
		<Selector
			isSearchable
			placeholder="Select a team"
			isDisabled={disabled}
			onChange={selectTeam}
			options={options}
			value={selectedTeam}
			className={className}
		/>
	);
}
